// A linha do tempo da ficha 360: a sequência inteira, agrupada por dia (H08). (#880)

import { useMemo, type ReactNode } from 'react';
import type { TipoDeItemDaTimeline } from '@cliqcrm/contracts';
import { horaDaMensagem } from '@/shared/lib/formato';
import { Carregando, Defasado, Erro, Vazio } from '@/shared/ui/estados/estados';
import type { NomeDoIcone } from '@/shared/ui';
import {
  agruparPorDia,
  type DiaDaTimeline,
  nomeDoTipo,
  mudancaDeEtapaEmPalavras,
  sinalDoTipo,
  TIPOS,
} from '../lib/linha-do-tempo';
import { type useTimelineDoCliente } from '../model/usar-timeline';
import { atoresEmPalavras, ehResumo, resumirMensagens } from '../lib/resumo-de-conversa';
import { Botao, Chip, FiltroPilula, Icone, ListaLinha, Tabela } from '@/shared/ui';

/** O que uma fileira da linha do tempo precisa, já pronto para desenhar. */
interface EventoDaTabela {
  readonly id: string;
  /** O tipo do contrato, que colore o disco: `mudanca_de_etapa`, `tarefa`, … */
  readonly tipo: string;
  readonly sinal: NomeDoIcone;
  readonly titulo: string;
  readonly ator: string | undefined;
  readonly hora: string;
  /** A célula "O que" — citação quando alguém escreveu, texto corrido quando não. */
  readonly corpo: ReactNode;
  /** O mesmo conteúdo em texto puro, para a prévia da lista do celular. */
  readonly textoDoCorpo: string | undefined;
}

/**
 * Os eventos de um dia, com as mensagens já resumidas em blocos de conversa.
 *
 * ── Uma linha no lugar de trinta ──────────────────────────────────────────
 *
 * O bloco de conversa responde "houve conversa aqui?" sem obrigar a ler a
 * conversa: quem falou, de que hora a que hora, e quantas mensagens. Quem
 * quiser ler abre, que é o mesmo clique de antes.
 *
 * O trecho é o da mensagem mais RECENTE do bloco — é a deixa de onde a conversa
 * parou, que é o que se procura ao bater o olho num histórico.
 */
function eventosDoDia(
  dia: DiaDaTimeline,
  aoAbrirConversa: (conversaId: string) => void,
): readonly EventoDaTabela[] {
  return resumirMensagens(dia.itens).map((item, indice) => {
    if (ehResumo(item)) {
      const quem = atoresEmPalavras(item.atores);
      const texto = `${item.quantidade} mensagens${item.ultimoTrecho ? ` · ${item.ultimoTrecho}` : ''}`;
      return {
        id: `resumo-${item.conversaId}-${item.de}-${indice}`,
        tipo: 'mensagem',
        sinal: sinalDoTipo('mensagem'),
        titulo: 'Conversa',
        ator: quem !== '' ? quem : undefined,
        // As duas pontas numa célula só: um intervalo precisa das duas para
        // dizer quanto durou, e a coluna é estreita demais para duas fileiras.
        hora: `${horaDaMensagem(item.de)} às ${horaDaMensagem(item.ate)}`,
        corpo: (
          <>
            <span className="c360__trecho">{texto}</span>
            <Botao miudo onClick={() => aoAbrirConversa(item.conversaId)}>
              Abrir a conversa
            </Botao>
          </>
        ),
        textoDoCorpo: texto,
      };
    }

    /*
     * A nota interna se ANUNCIA como interna, e não fica igual às outras.
     *
     * O que a equipe escreve ali é para a equipe — "não fecha por menos de 60
     * mil" ao lado de uma mensagem enviada, com a mesma aparência, faria alguém
     * ler como se o cliente tivesse visto.
     */
    const interna = item.tipo === 'nota';
    const mudanca = mudancaDeEtapaEmPalavras(item);
    const texto = mudanca ?? item.trecho ?? undefined;
    /*
     * Citação só quando ALGUÉM escreveu.
     *
     * Mensagem e nota são texto de outra pessoa dentro da nossa tela, e ganham
     * o fio à esquerda. "De Proposta para Negociação" é frase do sistema —
     * emoldurada, ela diria que alguém a escreveu, e num histórico isso é a
     * diferença entre o que o cliente falou e o que o sistema registrou.
     */
    const citacao = item.tipo === 'mensagem' || item.tipo === 'nota';

    return {
      id: `${item.tipo}-${item.ocorrido_em}-${indice}`,
      tipo: item.tipo,
      sinal: sinalDoTipo(item.tipo),
      titulo: interna ? 'Nota interna — o cliente não vê' : nomeDoTipo(item.tipo),
      ator: item.ator ?? undefined,
      hora: horaDaMensagem(item.ocorrido_em),
      corpo: texto ? (
        <>
          <span className={citacao ? 'c360__trecho' : 'c360__nota'}>{texto}</span>
          {item.conversa_id && (
            <Botao miudo onClick={() => aoAbrirConversa(item.conversa_id!)}>
              Abrir a conversa
            </Botao>
          )}
        </>
      ) : (
        '—'
      ),
      textoDoCorpo: texto,
    };
  });
}

export function LinhaDoTempo({
  timeline,
  cobertura,
  frescor,
  tipos,
  aoFiltrar,
  aoAbrirConversa,
}: {
  timeline: ReturnType<typeof useTimelineDoCliente>;
  cobertura: 'mensagens' | 'completa';
  frescor: { stale: boolean; projetado_em: string | null };
  tipos: readonly TipoDeItemDaTimeline[];
  aoFiltrar: (tipos: readonly TipoDeItemDaTimeline[]) => void;
  aoAbrirConversa: (conversaId: string) => void;
}): ReactNode {
  const dias = useMemo(() => agruparPorDia(timeline.itens), [timeline.itens]);

  function alternar(tipo: TipoDeItemDaTimeline): void {
    aoFiltrar(tipos.includes(tipo) ? tipos.filter((t) => t !== tipo) : [...tipos, tipo]);
  }

  return (
    /*
      Sem cabeçalho próprio: a ABA já se chama "Linha do tempo", e repetir a
      palavra 40px abaixo custa uma fileira inteira em 390px.
    */
    <>
      {cobertura === 'mensagens' && (
        <Chip tom="atencao" title="Só mensagens até a projeção completar">
          só mensagens
        </Chip>
      )}

      {frescor.stale && <Defasado projetadoEm={frescor.projetado_em} origem="A linha do tempo" />}

      {/* Filtro SUBTRATIVO, não abas. A armadilha da task é separar por tipo:
          o valor está em ver a sequência, e abas devolvem ao usuário o trabalho
          de correlacionar. */}
      <div className="c360__filtros" role="group" aria-label="Filtrar por tipo">
        {TIPOS.map(({ tipo, nome, sinal, alimentado }) => (
          <FiltroPilula
            key={tipo}
            miuda
            ligado={tipos.includes(tipo)}
            aoAlternar={() => alternar(tipo)}
            title={alimentado ? undefined : 'Este tipo ainda não tem origem — volta vazio'}
          >
            <Icone nome={sinal} /> {nome}
            {!alimentado && <span className="c360__sem-origem">sem origem</span>}
          </FiltroPilula>
        ))}
        {tipos.length > 0 && (
          <Botao variante="fantasma" miudo onClick={() => aoFiltrar([])}>
            Ver tudo
          </Botao>
        )}
      </div>

      {timeline.erro != null && (
        <Erro
          erro={timeline.erro}
          aoRepetir={timeline.carregarMais}
          titulo="Não foi possível carregar a linha do tempo"
        />
      )}

      {dias.length === 0 ? (
        timeline.carregando ? (
          <Carregando forma="lista" linhas={4} rotulo="Carregando a linha do tempo" />
        ) : (
          <Vazio
            titulo={tipos.length > 0 ? 'Nada desses tipos' : 'Nada registrado ainda'}
            porque={
              tipos.length > 0
                ? 'Nenhum fato desses tipos apareceu para este cliente. Veja tudo para conferir o resto.'
                : 'Mensagens, mudanças de etapa e tarefas deste cliente aparecem aqui em ordem.'
            }
          />
        )
      ) : (
        <>
          {/*
            A sequência é uma TABELA de quatro colunas, e o DIA é um `tbody`
            com a própria fileira de título.

            O que se perde do desenho de fio e disco é a sensação de corrente;
            o que se ganha é varrer a coluna "quem" e responder "quem falou com
            este cliente esta semana" sem ler evento por evento.
          */}
          <div className="c360__so-mouse">
            <Tabela
              densa
              rotulo="Linha do tempo deste cliente"
              colunas={[
                { chave: 'hora', rotulo: 'Hora' },
                { chave: 'evento', rotulo: 'Evento' },
                { chave: 'quem', rotulo: 'Quem' },
                { chave: 'oque', rotulo: 'O que' },
              ]}
              chaveDe={(linha) => String(linha['id'])}
              linhas={[]}
              grupos={dias.map((dia) => ({
                titulo: dia.rotulo,
                linhas: eventosDoDia(dia, aoAbrirConversa).map((e) => ({
                  id: e.id,
                  hora: <span className="c360__hora">{e.hora}</span>,
                  evento: (
                    <span className="c360__tempo-tipo">
                      <span className={`c360__sinal c360__sinal--${e.tipo}`} aria-hidden="true">
                        <Icone nome={e.sinal} />
                      </span>
                      {e.titulo}
                    </span>
                  ),
                  quem: e.ator ?? '—',
                  oque: e.corpo,
                })),
              }))}
            />
          </div>

          <ul className="c360__lista c360__so-cel">
            {dias.flatMap((dia) =>
              eventosDoDia(dia, aoAbrirConversa).map((e) => (
                <ListaLinha
                  key={e.id}
                  densa
                  inicio={
                    <span className={`c360__sinal c360__sinal--${e.tipo}`} aria-hidden="true">
                      <Icone nome={e.sinal} />
                    </span>
                  }
                  nome={e.titulo}
                  previa={e.textoDoCorpo}
                  meta={
                    <span className="c360__lista-meta">
                      <span>{dia.rotulo}</span>
                      {e.ator && <span>{e.ator}</span>}
                    </span>
                  }
                  direita={<span className="c360__hora">{e.hora}</span>}
                />
              )),
            )}
          </ul>
        </>
      )}

      {timeline.temMais && (
        <div className="c360__mais">
          <Botao
            variante="fantasma"
            onClick={timeline.carregarMais}
            desabilitado={timeline.carregando}
            motivo="Carregando…"
          >
            {timeline.carregando ? 'Carregando…' : 'Carregar o que veio antes'}
          </Botao>
        </div>
      )}
    </>
  );
}
